export interface BannerData {
  id: number | string;
  image: string;
  title?: string;
  isActive?: boolean;
}

// No static hardcoded fallback banners - all banners MUST come live from Database Server
export const DEFAULT_BANNERS: BannerData[] = [];

const STORAGE_KEY = 'asw_homepage_banners_v2';

const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '');
  }
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.') || hostname.startsWith('10.')) {
      return `${protocol}//${hostname}:5000`;
    }
  }
  return 'https://backend.aswcars.com';
};

const formatBannerImage = (img: string): string => {
  if (!img) return '';
  if (img.startsWith('/uploads/')) {
    const base = getApiUrl();
    return `${base}${img}`;
  }
  return img;
};

// Synchronous cached getter (returns local cache or empty array)
export const getBanners = (): BannerData[] => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map(b => ({ ...b, image: formatBannerImage(b.image) }));
      }
    }
  } catch (e) {
    console.error('Error reading cached banners from storage:', e);
  }
  return [];
};

// Async fetch live banners directly from server database API
export const fetchBannersFromApi = async (): Promise<BannerData[]> => {
  const baseUrl = getApiUrl();
  const urlsToTry = [
    `${baseUrl}/api/banners`,
    typeof window !== 'undefined' ? `${window.location.protocol}//${window.location.hostname}:5000/api/banners` : '',
    'http://localhost:5000/api/banners',
    'http://127.0.0.1:5000/api/banners',
    '/api/banners'
  ].filter(Boolean);

  for (const url of urlsToTry) {
    try {
      const response = await fetch(url);
      if (response.ok) {
        const result = await response.json();
        if (result && result.success && Array.isArray(result.banners)) {
          const formattedBanners = result.banners.map((b: any) => ({
            id: b.id,
            title: b.title,
            image: formatBannerImage(b.image || b.image_url),
            isActive: b.isActive !== false
          }));

          // Always overwrite local storage cache with live database server banners
          localStorage.setItem(STORAGE_KEY, JSON.stringify(formattedBanners));
          window.dispatchEvent(new Event('asw_banners_updated'));
          return formattedBanners;
        }
      }
    } catch (e) {
      // try next URL fallback
    }
  }
  return getBanners();
};

// Save banners locally and sync
export const saveBanners = (banners: BannerData[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(banners));
    window.dispatchEvent(new Event('asw_banners_updated'));
  } catch (e) {
    console.error('Error saving banners locally:', e);
  }
};

// Add new banner image to Server Database & Physical Uploads Folder
export const addBannerToApi = async (banner: { title?: string; image: string }): Promise<BannerData[]> => {
  const baseUrl = getApiUrl();
  const url = `${baseUrl}/api/banners`;
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: banner.title || 'New Banner',
        image: banner.image,
        is_active: true
      })
    });
    if (response.ok) {
      return await fetchBannersFromApi();
    }
  } catch (e) {
    console.error('Error uploading banner to server API:', e);
  }
  return await fetchBannersFromApi();
};

// Toggle banner status in Server Database
export const toggleBannerInApi = async (id: number | string, currentStatus: boolean): Promise<BannerData[]> => {
  const baseUrl = getApiUrl();
  const url = `${baseUrl}/api/banners/${id}`;
  try {
    const response = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ isActive: !currentStatus })
    });
    if (response.ok) {
      return await fetchBannersFromApi();
    }
  } catch (e) {
    console.error('Error toggling banner on server API:', e);
  }
  return await fetchBannersFromApi();
};

// Delete banner from Server Database
export const deleteBannerFromApi = async (id: number | string): Promise<BannerData[]> => {
  const baseUrl = getApiUrl();
  const url = `${baseUrl}/api/banners/${id}`;
  try {
    const response = await fetch(url, {
      method: 'DELETE'
    });
    if (response.ok) {
      return await fetchBannersFromApi();
    }
  } catch (e) {
    console.error('Error deleting banner from server API:', e);
  }
  return await fetchBannersFromApi();
};
